import React, {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import { type DefaultValues, type FieldValues, useForm, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useBlocker, useLocation, useNavigate } from 'react-router-dom';
import { Box, Paper, Stack, type SxProps, type Theme, useTheme } from '@mui/material';
import { mergeSx } from 'merge-sx';
import { match } from 'path-to-regexp';
import { Schema } from 'yup';

import BackdropLoader from '@/components/BackdropLoader';
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal';
import Form, { CommonFormFooter, FormLoader } from '@/components/Form/Form';
import { VerticalTab, VerticalTabs } from '@/components/Tabs';
import { useSnackbar } from '@/hooks/useSnackbar';
import { yupValidationResolver } from '@/hooks/useYupValidationResolver';
import { SETTINGS_STEP_KEYS, toOrganizationSettings } from '@/root/linker';
import { normalizePathname } from '@/utils/normalizePathname';

import { useClipBottom } from './hooks/useClipBottom';

interface SettingsTab {
  stepKey: SETTINGS_STEP_KEYS;
  label: string;
  link: string;
  disabled?: boolean;
}

interface SettingsShellProps<TFieldValues extends FieldValues> {
  tabs: SettingsTab[];
  children: ReactNode;
  defaultValues: DefaultValues<TFieldValues>;
  defaultRoute?: string;
  routePattern: string;
  sx?: SxProps<Theme>;
  onSave?: <Values extends object>(values: Values, stepKey: SETTINGS_STEP_KEYS) => Promise<void>;
  validations?: { [key: string]: Schema<TFieldValues> };
  hideFooter?: boolean;
}

const FORM_HEADER_HEIGHT_PX = 70;
const FORM_BOTTOM_PADDING = 2;
const FORM_SHADOW_SPACE = 1;
const SIDE_NAVIGATION_WIDTH_PX = 233;

export interface SettingsShellRef<T extends FieldValues> {
  setValue: UseFormSetValue<T>;
  reset: () => void;
  getValues: () => FieldValues;
  watch: UseFormWatch<T>;
}

const SettingsShellInner = <T extends FieldValues>(
  { tabs, children, defaultValues, defaultRoute, routePattern, onSave, sx, validations }: SettingsShellProps<T>,
  ref: React.Ref<SettingsShellRef<T>>
) => {
  const { showMessage } = useSnackbar();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { spacing } = useTheme();
  const { t } = useTranslation(['settings', 'validation']);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [lastSavedStep, setLastSavedStep] = useState<SETTINGS_STEP_KEYS | null>(null);
  const blockerBypassRef = useRef(false);

  const pathMatcher = useMemo(() => match(routePattern), [routePattern]);
  const normalizedPathname = useMemo(() => normalizePathname(routePattern, pathname), [routePattern, pathname]);

  const { organizationSlug, stepKey } = useMemo(() => {
    const matchResult = pathMatcher(normalizedPathname) as unknown as {
      params: { stepKey?: string; organizationSlug?: string };
    };
    if (!matchResult) return {};

    return matchResult?.params || {};
  }, [pathMatcher, normalizedPathname]);

  const resolver = useMemo(() => yupValidationResolver(validations?.[stepKey!]), [stepKey, validations]);
  const form = useForm({ defaultValues, reValidateMode: 'onChange', resolver });
  const {
    formState: { dirtyFields },
    getValues
  } = form;
  const hasDirtyFields = !!Object.keys(dirtyFields).length;
  const [formBodyRef] = useClipBottom<HTMLDivElement>(FORM_SHADOW_SPACE, FORM_BOTTOM_PADDING);

  useImperativeHandle(ref, () => ({
    setValue: form.setValue,
    reset: () => form.reset(),
    getValues: () => form.getValues(),
    watch: form.watch
  }));

  useEffect(() => {
    form.reset(defaultValues, { keepDirty: false });
  }, [defaultValues]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!stepKey && defaultRoute) {
      navigate(defaultRoute);
    }
  }, [stepKey, defaultRoute, navigate]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (blockerBypassRef.current) {
      blockerBypassRef.current = false;

      return false;
    }
    const isSameLocation =
      currentLocation.pathname === nextLocation.pathname ||
      normalizePathname(routePattern, currentLocation.pathname) ===
        normalizePathname(routePattern, nextLocation.pathname);
    const isBlocked = hasDirtyFields && !isSameLocation;
    isBlocked && setShowConfirmationModal(true);

    return isBlocked;
  });

  const onSaveCallback = useCallback(async () => {
    try {
      const data = getValues();
      setLastSavedStep(stepKey as SETTINGS_STEP_KEYS);
      await onSave?.(data, stepKey as SETTINGS_STEP_KEYS);
      showMessage(t('settings.alerts.success'));
    } catch (e) {
      showMessage(t('settings.alerts.error'));
    }
  }, [stepKey, onSave, getValues, showMessage, t]);

  const onNavigationConfirm = useCallback(async () => {
    await onSaveCallback();
    setShowConfirmationModal(false);
    blocker.proceed?.();
  }, [blocker, onSaveCallback]);

  const onNavigationCancel = useCallback(() => {
    setShowConfirmationModal(false);
    blocker.reset?.();
  }, [blocker]);

  const onNavigationFailedConfirm = useCallback(() => {
    showMessage(t('validation:alerts.failed'));
    onNavigationCancel();
  }, [onNavigationCancel, showMessage, t]);

  const onNavigationDicsard = useCallback(() => {
    setShowConfirmationModal(false);
    form.reset();
    blocker.proceed?.();
  }, [blocker, form]);

  const handleFormReset = useCallback(() => {
    form.reset();
    if (!lastSavedStep) return;

    blockerBypassRef.current = true;
    navigate(toOrganizationSettings({ organizationSlug: organizationSlug!, stepKey: lastSavedStep }));
    setLastSavedStep(null);
  }, [form, lastSavedStep, navigate, organizationSlug]);

  if (!defaultValues) return null;

  return (
    <Form form={form} onSubmit={onSaveCallback} sx={mergeSx(sx, { height: '100%' })}>
      <Stack
        direction="row"
        sx={{ flexGrow: 1, height: `calc(100% - ${FORM_HEADER_HEIGHT_PX}px - ${spacing(FORM_BOTTOM_PADDING)})` }}
      >
        <VerticalTabs
          variant="scrollable"
          value={stepKey}
          sx={{ mt: 3, width: SIDE_NAVIGATION_WIDTH_PX, minWidth: SIDE_NAVIGATION_WIDTH_PX, minHeight: 'unset' }}
        >
          {tabs.map(tab => (
            <VerticalTab
              key={tab.stepKey}
              label={t(tab.label)}
              value={tab.stepKey}
              component={tab.disabled ? Box : Link}
              to={tab.link}
              disabled={tab.disabled}
            />
          ))}
        </VerticalTabs>
        <Stack flexGrow={1}>
          <Paper
            ref={formBodyRef}
            component={Stack}
            mb={0}
            pb={`calc(${spacing(4 + FORM_BOTTOM_PADDING)})`}
            flex={1}
            elevation={3}
            sx={{ bgcolor: 'background.default' }}
          >
            <FormLoader fallback={<BackdropLoader />}>
              <Stack flexGrow={1}>{children}</Stack>
            </FormLoader>
          </Paper>
          <Paper
            component={Stack}
            elevation={3}
            sx={{
              bgcolor: 'background.default',
              position: 'sticky',
              bottom: spacing(FORM_BOTTOM_PADDING),
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0
            }}
          >
            <CommonFormFooter onReset={handleFormReset} disableSubmit={!onSave} />
          </Paper>
        </Stack>
      </Stack>

      <ConfirmationModal
        open={showConfirmationModal}
        onConfirm={form.handleSubmit(onNavigationConfirm, onNavigationFailedConfirm)}
        onClose={onNavigationCancel}
        onDiscard={onNavigationDicsard}
        discardButtonLabel={t('settings.discardButton')}
        title={t('settings.unsavedChangesTitle')}
      />
    </Form>
  );
};

const SettingsShell = forwardRef(SettingsShellInner);

SettingsShell.displayName = 'SettingsShell';

export default SettingsShell;
